import { LitElement, html, css, type PropertyValues } from 'lit'
import { provide } from '@lit/context'
import { breakpointContext, navigationContext, displayModeContext, isSubMenuOpenContext, isScrolledTopContext, isOutletNavigationOpenContext } from './bmw-header/context.ts'
import { customElement, property, state } from 'lit/decorators.js'
import { TW } from '../shared/tailwindMixin'

import { createFocusTrap } from 'focus-trap'
import { getBreakpoint, ScrollTopController, BreakpointController, AdminBarHeightController, ScrollLockController } from '../shared'
import type { Breakpoint, MenuItem } from '../shared'

const TwLitElement = TW(LitElement)

type FocusTrapInstance = ReturnType<typeof createFocusTrap>

/**
 * Sample Lit Element in assets/lit
 */
@customElement('bmw-header')
export class BmwHeader extends TwLitElement {

    @property({ type: String, attribute: 'initial-mode' }) 
    initialDisplayMode?: string

    @property({ type: String }) 
    headline = "BMW Mustermann"

    @property({ type: String }) 
    subheadline = "Musterhausen"

    @property({ type: Boolean, attribute: 'has-multiple-locations' })
    hasMultipleLocations: boolean = false

    @property({ type: Array }) 
    data: MenuItem[] = []

    @provide({context: navigationContext})
    @state()
    private _data: MenuItem[] = []

    @provide({context: displayModeContext})
    @state()
    private _displayMode: string = "white"

    @provide({context: breakpointContext})
    @state() 
    // @ts-expect-error - Property wird benötigt, um scope zu befüllen - aber in der Komponente nicht gelesen
    private _breakpoint: Breakpoint | null = getBreakpoint()

    @provide({context: isScrolledTopContext}) 
    @state()
    private _isScrolledTop: boolean = true;

    @provide({context: isSubMenuOpenContext})
    @state()
    private _isSubMenuOpen: boolean = false

    @provide({context: isOutletNavigationOpenContext})
    @state()
    private _isOutletNavigationOpen: boolean = false

    @state()
    private _adminBarHeight: number = 0

    // @ts-expect-error - Property wird benötigt, um Controller-Referenz zu halten, wird aber nicht direkt gelesen
    private _scrollTopController: ScrollTopController

    // @ts-expect-error - Property wird benötigt, um Controller-Referenz zu halten, wird aber nicht direkt gelesen
    private _breakpointController: BreakpointController

    // @ts-expect-error - Property wird benötigt, um Controller-Referenz zu halten, wird aber nicht direkt gelesen
    private _adminBarHeightController: AdminBarHeightController

    private _scrollLockController: ScrollLockController

    private _menuFocusTrap: FocusTrapInstance | null = null

    static styles = css`
        #sentinel { /* Used for scroll observer */
            display: block;
            position: absolute;
            top: -1px;
            left: 0;
            width: 100%;
            height: 1px;
        }
    `;

    constructor() 
    {
        super();

        // Event Listeners für Nachrichten von den Kind-Elementen

        this.addEventListener( 'bmw-header-open-sub-menu', event => this._onOpenSubMenu( event as CustomEvent ) )
        this.addEventListener( 'bmw-header-close-menus', event => this._onCloseMenus( event as CustomEvent ) )
        this.addEventListener( 'bmw-header-toggle-menu-item', event => this._onToggleMenuItem( event as CustomEvent ) )
        this.addEventListener( 'bmw-header-toggle-outlet-navigation', event => this._onToggleOutletNavigation( event as CustomEvent ) )
        
        // Initialisiere BreakpointController mit Callback

        this._breakpointController = new BreakpointController(
            this,
            (breakpoint) => {
                this._breakpoint = breakpoint
            }
        )

        // Initialisiere ScrollTopController mit Callback
        this._scrollTopController = new ScrollTopController(
            this,
            (isScrolledTop) => {
                this._isScrolledTop = isScrolledTop
            }
        )

        // Initialisiere AdminBarHeightController mit Callback
        this._adminBarHeightController = new AdminBarHeightController(
            this,
            (height) => {
                this._adminBarHeight = height
            }
        )

        // Initialisiere ScrollLockController
        this._scrollLockController = new ScrollLockController(this)
    }

    willUpdate(changedProperties: PropertyValues) 
    {
        // Initialisiere den _data Status mit den Daten aus der Property data und füge das active property hinzu
        
        if ( changedProperties.has('data') ) {
            this._data = this.data.map( item => { item.active = false; return item } ) 
        }

        // Initialisiere den _displayMode Status mit dem initialDisplayMode Property
        
        if (changedProperties.has('initialDisplayMode')) {
            this._displayMode = this.initialDisplayMode ?? "white"
        }

        // DisplayMode zentral berechnen:
        // - Sobald ein Menü offen ist => immer weiß
        // - Wenn kein Menü offen ist:
        //    - am Seitenanfang => initialDisplayMode (z.B. transparent bei Media-Block)
        //    - sonst => weiß
        if (
            changedProperties.has('_isScrolledTop') ||
            changedProperties.has('_isSubMenuOpen') ||
            changedProperties.has('_isOutletNavigationOpen') ||
            changedProperties.has('initialDisplayMode') ||
            changedProperties.has('_data')
        ) {
            const menuOpen = this._isSubMenuOpen || this._isOutletNavigationOpen
            if (menuOpen) {
                this._displayMode = 'white'
            } else if (this._isScrolledTop) {
                this._displayMode = this.initialDisplayMode ?? 'white'
            } else {
                this._displayMode = 'white'
            }
        }

        // Scroll-Lock Management: Reagiere auf Menü-Öffnen/Schließen
        const menuIsOpen = this._isSubMenuOpen || this._isOutletNavigationOpen
        const menuWasClosed = (changedProperties.has('_isSubMenuOpen') && !this._isSubMenuOpen) || 
            (changedProperties.has('_isOutletNavigationOpen') && !this._isOutletNavigationOpen)

        if (menuIsOpen && (changedProperties.has('_isSubMenuOpen') || changedProperties.has('_isOutletNavigationOpen'))) {
            // Menü wurde geöffnet: Lock aktivieren
            this._scrollLockController.lock()
        } else if (menuWasClosed && !menuIsOpen) {
            // Menü wurde geschlossen: Lock deaktivieren
            this._scrollLockController.unlock()
        }
    }

    /**
     * Wird nach jedem Update aufgerufen - hier berechnen wir die Menü-Höhe und passen die Scroll-Position an,
     * und aktivieren/deaktivieren den Keyboard-Focus-Trap für das Menü.
     */
    updated() {
        const menuOpen = this._isSubMenuOpen || this._isOutletNavigationOpen
        if (menuOpen) {
            // Warte kurz, damit das DOM vollständig gerendert ist
            requestAnimationFrame(() => {
                this._adjustScrollPositionForMenu()
                this._activateMenuFocusTrap()
            })
        } else {
            this._deactivateMenuFocusTrap()
        }
    }

    /**
     * Aktiviert einen Focus-Trap auf dem Menü-Container (WCAG: Tastatur bleibt im Menü).
     */
    private _activateMenuFocusTrap(): void {
        if (this._menuFocusTrap) return
        // Container umfasst Oberkante (inkl. Schließen-Button) und Menü-Inhalt, damit der Close-Button im Trap liegt
        const container = this.shadowRoot?.querySelector('header .container') as HTMLElement
        if (!container) return
        const trap = createFocusTrap(container, {
            escapeDeactivates: true,
            returnFocusOnDeactivate: true,
            tabbableOptions: {
                getShadowRoot: (node: Element) => (node as unknown as { shadowRoot: ShadowRoot | null }).shadowRoot ?? undefined,
            },
            onDeactivate: () => {
                this._isSubMenuOpen = false
                this._isOutletNavigationOpen = false
                this._menuFocusTrap = null
                this.requestUpdate()
            },
        })
        this._menuFocusTrap = trap
        trap.activate()
    }

    /**
     * Deaktiviert den Menü-Focus-Trap (z. B. nach Klick auf Schließen).
     */
    private _deactivateMenuFocusTrap(): void {
        const trap = this._menuFocusTrap
        this._menuFocusTrap = null
        if (trap) trap.deactivate()
    }

    /**
     * Berechnet die Menü-Höhe und setzt eine maximale Höhe für den Menü-Container,
     * damit bei zu langem Inhalt ein Scrollbalken im Menü-Bereich erscheint.
     */
    private _adjustScrollPositionForMenu(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        const menuContainer = this.shadowRoot?.querySelector('.bmw-header-menu-container') as HTMLElement

        if (!menuContainer) {
            return
        }

        // Verfügbarer Platz ab der Oberkante des Menü-Containers bis zum unteren Viewport-Rand
        const containerRect = menuContainer.getBoundingClientRect()
        const availableHeight = Math.max(0, window.innerHeight - containerRect.top)

        // Wenn kein Menü offen ist, entfernen wir evtl. gesetzte Styles
        if (!this._isSubMenuOpen && !this._isOutletNavigationOpen) {
            menuContainer.style.maxHeight = ''
            menuContainer.style.overflowY = ''
            return
        }

        // Menü-Inhalt-Höhe ermitteln (nur der Navigations-/Outlet-Teil, nicht der Branding-Bereich)
        let menuContentHeight = 0

        if (this._isSubMenuOpen) {
            const desktopNav = this.shadowRoot?.querySelector('desktop-navigation') as HTMLElement | null
            const mobileNav = this.shadowRoot?.querySelector('mobile-navigation') as HTMLElement | null

            if (desktopNav && !desktopNav.hasAttribute('hidden')) {
                menuContentHeight = desktopNav.offsetHeight
            } else if (mobileNav && !mobileNav.hasAttribute('hidden')) {
                menuContentHeight = mobileNav.offsetHeight
            }
        } else if (this._isOutletNavigationOpen) {
            const slot = this.shadowRoot?.querySelector('slot')
            if (slot) {
                const assignedElements = slot.assignedElements()
                if (assignedElements.length > 0) {
                    menuContentHeight = assignedElements.reduce((sum, el) => {
                        return sum + (el as HTMLElement).offsetHeight
                    }, 0)
                }
            }
        }

        // Wenn der verfügbare Platz ausreicht, um den Menüinhalt komplett zu zeigen,
        // brauchen wir keinen eigenen Scrollbalken im Menü-Container.
        if (menuContentHeight <= availableHeight) {
            menuContainer.style.maxHeight = ''
            menuContainer.style.overflowY = ''
            return
        }

        // Menüinhalt ist höher als der verfügbare Platz: interner Scroll im Menü-Container
        menuContainer.style.maxHeight = `${availableHeight}px`
        menuContainer.style.overflowY = 'auto'
    }

    /**
     * Dieser Handler wird von allen MenuLinks mit Kindern aufgerufen und toggled den State in der Menustruktur entsprechend
     * 
     * @param event 
     */
    private _onToggleMenuItem(event: CustomEvent) 
    {
        event.preventDefault();
        event.stopPropagation();

        // Zuerst suchen wir das geklickte MenuItem

        const navItem = event.detail.navItem
        const clickedMenuItem = this._data.find( item => item.id === navItem.id )

        if ( ! clickedMenuItem ) {
            return
        }

        if ( ! clickedMenuItem.active ) {

            // Beim Aktivieren eines MenuPunkts werden zuerst alle MenuItems deaktiviert und 
            // danach das geklickte MenuItem und alle seine Ancestors aktiviert

            let newData = this._data.map( item => { item.active = false; return item } )

            const idsToActivate = new Set<number>();
            let currentId: number | undefined = clickedMenuItem.id;

            // Den Pfad von der Ziel-ID bis zur Root ermitteln
            while (currentId !== undefined && currentId !== 0) {
                idsToActivate.add(currentId);
                const currentItem = this._data.find(item => item.id === currentId);
                currentId = currentItem?.parentId;
            }

            // Jetzt aktivieren wir noch das Submenu

            this._isSubMenuOpen = true

            // Eine Kopie des Arrays erstellen und die betroffenen Items aktivieren

            this._data = newData.map(item => {
                if (idsToActivate.has(item.id)) {
                    return { ...item, active: true };
                }
                return item;
            });

            return
        } 

        // Beim Deaktivieren eines MenuPunkts wird 
        // das geklickte MenuItem und alle seine Descendants deaktiviert

        const idsToDeactivate = new Set<number>();

        // Rekursive Funktion, um alle Nachkommen (Kinder, Enkel, etc.) zu sammeln
        const collectDescendants = (parentId: number) => {
            idsToDeactivate.add(parentId);
            // Finde alle direkten Kinder dieses Items
            const children = this._data.filter(item => item.parentId === parentId);
            // Rekursiv alle Nachkommen der Kinder sammeln
            children.forEach(child => collectDescendants(child.id));
        };

        // Starte die Sammlung beim geklickten Item
        collectDescendants(clickedMenuItem.id);

        // Falls 
        // - das geklickte MenuItem level 0, 
        // - das geklickte MenuItem aktiv ist und 
        // - im Event detail alsoToggleSubMenu auf true gesetzt ist 
        // schließen wir das Submenu

        if (clickedMenuItem.level === 0 && clickedMenuItem.active && event.detail.alsoToggleSubMenu) {
            console.log( 'bmw-header.onToggleMenuItem close submenu' )
            this._isSubMenuOpen = false
        }

        // Erstelle eine neue Kopie der Daten und deaktiviere alle betroffenen Items

        this._data = this._data.map(item => {
            if (idsToDeactivate.has(item.id)) {
                return { ...item, active: false };
            }
            return item;
        });

        return
    }

    private _onCloseMenus(event: CustomEvent) 
    {
        event.preventDefault();
        event.stopPropagation();

        // Falls das Submenu offen ist, deaktivieren wir zuerst alle aktiven Items

        this._data = this._data.map(item => {
            return { ...item, active: false };
        });

        // Jetzt schließen wir beide Menus

        this._isSubMenuOpen = false
        this._isOutletNavigationOpen = false
    }

    private _onOpenSubMenu(event: CustomEvent) 
    {
        event.preventDefault();
        event.stopPropagation();
        this._isSubMenuOpen = true
    }

    private _onToggleOutletNavigation(event: CustomEvent) 
    {
        event.preventDefault();
        event.stopPropagation();
        this._isOutletNavigationOpen = ! this._isOutletNavigationOpen
    }

    render() 
    {

        // this._isOutletNavigationOpen = true
        // this._displayMode = 'white'

        let containerClasses = []
        this._displayMode === 'white' 
            ? containerClasses.push('bg-white', 'text-default-text')
            : containerClasses.push('text-white')

        let overlayClasses = []
        this._displayMode === 'white' 
            ? overlayClasses.push('bg-white')
            : overlayClasses.push('bg-linear-to-b', 'from-black/90', 'via-black/60','to-black/0')    

        return html`
            <div id="sentinel"></div>
            <header 
                role="banner"
                aria-label="Seitenkopf"
                class="fixed left-0 right-0 z-50 flex flex-col ${this._isSubMenuOpen || this._isOutletNavigationOpen ? 'bg-white h-full' : ''}"
                style="top: ${this._adminBarHeight}px"
            > 
                <div class="container ${containerClasses.join(' ')} transition-all duration-300 ease-in-out">
                    <div class="alignfull row-start-1 row-end-2 z-10 ${overlayClasses.join(' ')}"></div>
                    <div class="alignwide row-start-1 row-end-2 z-20 pt-6 flex justify-between items-end mb-0! border-b border-b-[rgb(187,187,187)]">
                        <bmw-branding
                            headline=${this.headline}
                            subheadline=${this.subheadline}
                        ></bmw-branding>
                        <desktop-top-level-nav></desktop-top-level-nav>
                        <nav aria-label="Menü und Aktionen">
                            <ul class="flex gap-2 items-center">
                                <li class="py-4 border-b-4 border-b-transparent">
                                    ${
                                        this.hasMultipleLocations 
                                        ? html`<button-open-outlet-menu @click=${this._onToggleOutletNavigation} text="Standorte & Kontakt"></button-open-outlet-menu>` 
                                        : html`<button-open-outlet-menu @click=${this._onToggleOutletNavigation} text="Standort & Kontakt"></button-open-outlet-menu>`
                                    }
                                </li>
                                <li class="py-4 border-b-4 border-b-transparent"><button-open-menu></button-open-menu></li>
                                <li class="py-4 border-b-4 border-b-transparent"><button-close-menus></button-close-menus></li>
                            </ul>
                        </nav>
                    </div>
                    <div 
                        class="bmw-header-menu-container alignbreakout"
                        role="${this._isSubMenuOpen || this._isOutletNavigationOpen ? 'dialog' : undefined}"
                        aria-modal="${this._isSubMenuOpen || this._isOutletNavigationOpen ? 'true' : undefined}"
                        aria-label="${this._isSubMenuOpen || this._isOutletNavigationOpen ? (this._isOutletNavigationOpen ? 'Standorte und Kontakt' : 'Hauptmenü') : undefined}"
                    >
                        <desktop-navigation></desktop-navigation>
                        <mobile-navigation></mobile-navigation>
                        ${ this._isOutletNavigationOpen ? html`<slot></slot>` : html`` }
                    </div>
                </div>
            </header>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'bmw-header': BmwHeader
    }
}
